import { useState } from 'react'
import { Eye, ShieldCheck, UserCheck, Users as UsersIcon } from 'lucide-react'
import { Badge, Button, Card, CardContent } from '@/components/ui'
import { SectionHeading } from '@/components/common'
import { useAdminUsers } from '../../hooks/use-admin'
import { EnterpriseTable, type Column } from '../../components/tables/enterprise-table'
import { UserDetailDrawer } from '../../components/dialogs/user-detail-drawer'
import type { AdminUser } from '../../types/admin.types'

export function UsersPage() {
  const { data: usersOverview, isLoading } = useAdminUsers()
  const [selectedUser, setSelectedUser] = useState<AdminUser | null>(null)
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)

  const handleInspectUser = (user: AdminUser) => {
    setSelectedUser(user)
    setIsDrawerOpen(true)
  }

  const columns: Column<AdminUser>[] = [
    {
      key: 'name',
      header: 'User',
      sortable: true,
      sortValue: (u) => `${u.firstName} ${u.lastName}`,
      accessor: (u) => (
        <div className="flex items-center gap-3">
          <div className="flex size-9 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary font-display text-xs font-bold">
            {u.firstName?.[0]}
            {u.lastName?.[0]}
          </div>
          <div className="min-w-0">
            <p className="truncate font-semibold text-foreground">
              {u.firstName} {u.lastName}
            </p>
            <p className="truncate text-xs text-muted-foreground">{u.email}</p>
          </div>
        </div>
      ),
    },
    {
      key: 'role',
      header: 'Role',
      sortable: true,
      accessor: (u) => (
        <Badge variant={u.role === 'ADMIN' ? 'destructive' : u.role === 'DEALER' ? 'default' : 'secondary'}>
          {u.role}
        </Badge>
      ),
    },
    {
      key: 'approvalStatus',
      header: 'Approval',
      sortable: true,
      accessor: (u) => (
        <Badge
          variant={
            u.approvalStatus === 'ACTIVE' || u.approvalStatus === 'APPROVED'
              ? 'success'
              : u.approvalStatus === 'PENDING'
              ? 'warning'
              : 'destructive'
          }
        >
          {u.approvalStatus}
        </Badge>
      ),
    },
    {
      key: 'accountType',
      header: 'Account Type',
      sortable: true,
      accessor: (u) => <span className="capitalize text-xs font-bold text-muted-foreground">{u.accountType}</span>,
    },
    {
      key: 'createdAt',
      header: 'Registered',
      sortable: true,
      sortValue: (u) => new Date(u.createdAt).getTime(),
      accessor: (u) => (
        <span className="text-xs text-muted-foreground">
          {new Date(u.createdAt).toLocaleDateString()}
        </span>
      ),
    },
  ]

  const filterOptions = [
    {
      key: 'role',
      label: 'Role',
      options: [
        { label: 'Customer', value: 'CUSTOMER' },
        { label: 'Seller', value: 'SELLER' },
        { label: 'Dealer', value: 'DEALER' },
        { label: 'Administrator', value: 'ADMIN' },
      ],
      getValue: (u: AdminUser) => u.role,
    },
    {
      key: 'approvalStatus',
      label: 'Approval Status',
      options: [
        { label: 'Active', value: 'ACTIVE' },
        { label: 'Pending', value: 'PENDING' },
        { label: 'Rejected', value: 'REJECTED' },
      ],
      getValue: (u: AdminUser) => u.approvalStatus,
    },
  ]

  return (
    <div className="space-y-8">
      {/* Header */}
      <SectionHeading
        eyebrow="User Directory"
        title="User Management"
        description="Search, filter, inspect profiles, and manage system permissions across all accounts."
        align="left"
      />

      {/* Summary Cards */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Card className="border-border/80 bg-card/80 backdrop-blur-xl">
          <CardContent className="p-4 flex items-center gap-3">
            <span className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
              <UsersIcon className="size-5" />
            </span>
            <div>
              <p className="text-xs text-muted-foreground font-medium">Total Registered</p>
              <p className="font-display text-xl font-bold text-foreground">
                {usersOverview?.counts.total ?? 0}
              </p>
            </div>
          </CardContent>
        </Card>

        <Card className="border-border/80 bg-card/80 backdrop-blur-xl">
          <CardContent className="p-4 flex items-center gap-3">
            <span className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-emerald-500/10 text-emerald-500">
              <UserCheck className="size-5" />
            </span>
            <div>
              <p className="text-xs text-muted-foreground font-medium">Active Accounts</p>
              <p className="font-display text-xl font-bold text-foreground">
                {usersOverview?.counts.active ?? 0}
              </p>
            </div>
          </CardContent>
        </Card>

        <Card className="border-border/80 bg-card/80 backdrop-blur-xl">
          <CardContent className="p-4 flex items-center gap-3">
            <span className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-amber-500/10 text-amber-500">
              <ShieldCheck className="size-5" />
            </span>
            <div>
              <p className="text-xs text-muted-foreground font-medium">Pending Verification</p>
              <p className="font-display text-xl font-bold text-amber-500">
                {usersOverview?.counts.approval.pending ?? 0}
              </p>
            </div>
          </CardContent>
        </Card>

        <Card className="border-border/80 bg-card/80 backdrop-blur-xl">
          <CardContent className="p-4 flex items-center gap-3">
            <span className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-purple-500/10 text-purple-500">
              <UsersIcon className="size-5" />
            </span>
            <div>
              <p className="text-xs text-muted-foreground font-medium">Sellers & Dealers</p>
              <p className="font-display text-xl font-bold text-foreground">
                {(usersOverview?.counts.sellers ?? 0) + (usersOverview?.counts.dealers ?? 0)}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Users Table */}
      <EnterpriseTable
        data={usersOverview?.recentUsers ?? []}
        columns={columns}
        isLoading={isLoading}
        searchPlaceholder="Search by name or email..."
        searchableKeys={[(u) => `${u.firstName} ${u.lastName} ${u.email}`]}
        filterOptions={filterOptions}
        actions={(u) => (
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleInspectUser(u)}
            className="h-8 gap-1.5 text-xs font-semibold"
          >
            <Eye className="size-3.5" /> View Profile
          </Button>
        )}
        emptyTitle="No users found"
        emptyDescription="Try clearing your search query or filters."
      />

      {/* Profile Detail Drawer */}
      <UserDetailDrawer
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        user={selectedUser}
      />
    </div>
  )
}
